import { Fragment, useMemo } from 'react';
import Head from 'next/head';
import _get from 'lodash/get';
import { Form, Formik } from 'formik';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import Main from '../../components/main';
import { buildTitle } from '../../utils/layout';
import CalendarHeader from '../../components/calendar/header';
import { useAppContext } from '../../contexts/app';
import { nearestCityQuery } from '../../apollo/auth';
import MaterialLink from '../../components/material-link';
import CalendarLinks from '../../components/calendar-links';
import RadioGroupField from '../../components/forms/radio-group-field';

const useStyles = makeStyles((theme) => ({
  divider: {
    margin: theme.spacing(3, 0),
  },
}));

const CalendarSubscribePage = () => {
  const classes = useStyles();
  const { loggedInUser, isLoggedIn } = useAppContext();

  const {
    data: nearestCityData,
    loading: nearestCityLoading,
  } = useQuery(nearestCityQuery, {
    skip: isLoggedIn,
  });
  const nearestCity = _get(nearestCityData, 'nearestCity');

  const city = useMemo(() => {
    if (loggedInUser) {
      return loggedInUser.primaryCity;
    }
    return nearestCity;
  }, [loggedInUser, nearestCity]);

  return (
    <Main>
      <Head>
        <title>{buildTitle('Subscribe to Calendar')}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <CalendarHeader title="Calendar" />

      <Typography variant="h2">Subscribe to Calendar</Typography>

      {nearestCityLoading && <p>Loading...</p>}

      <Formik
        initialValues={{
          calendarType: isLoggedIn ? 'ALL' : 'CITY',
        }}
      >
        {({ values }) => {
          let path;
          if (values.calendarType === 'CITY') {
            path = `/cities/${_get(city, 'slug')}/calendar.ics`;
          } else {
            const query = values.calendarType === 'FOLLOWING' ? '?filter=following' : '';
            path = `/calendars/${loggedInUser.calendarToken}/calendar.ics${query}`;
          }

          return (
            <Form>
              <RadioGroupField
                name="calendarType"
                label="Include in Calendar Feed"
                disabled={!isLoggedIn}
                options={[
                  {
                    label: (
                      <Fragment>
                        City and Personalized (my groups and my following){' '}(<strong>recommended</strong>)
                      </Fragment>
                    ),
                    value: 'ALL',
                  },
                  {
                    label: 'Personalized (my groups and my following) Only',
                    value: 'FOLLOWING',
                  },
                  {
                    label: 'City Only',
                    value: 'CITY',
                  },
                ]}
              />
              <p>Calendar links below include {_get(city, 'name')} events{values.includeMyEvents && ' and my events'}.</p>
              <CalendarLinks path={path} />
            </Form>
          );
        }}
      </Formik>

      <p><strong>NOTE:</strong> RSVP &quot;Not Going&quot; to events you&apos;re not interested to have them removed from your calendar subscription.</p>
      
      <Divider className={classes.divider} />

      <p>Group-only calendar feeds can be found at the bottom of each group&apos;s calendar page.</p>

      {loggedInUser && (
        <Fragment>

          <Divider className={classes.divider} />

          <p>Everyday Rides also sends weekly reminder emails that you can manage in your <MaterialLink href="/account">notification preferences</MaterialLink>.</p>
        </Fragment>
      )}
    </Main>
  );
};

export default CalendarSubscribePage;
